import { useMemo, useState } from "react";
import { DateTime } from "luxon";

const initialOperators = {
  default: (dateFormat, timeFormat) => {
    const now = DateTime.now();
    const startValue = now.minus({ minute: 30 });

    return {
      endDate: now.toFormat(dateFormat),
      endTime: now.toFormat(timeFormat),
      startDate: startValue.toFormat(dateFormat),
      startTime: startValue.toFormat(timeFormat),
    };
  },
};

const initialValue = {
  endDate: "",
  endTime: "",
  startDate: "",
  startTime: "",
};

const useDateTimeRange = ({
  dateFormat = "yyyy-MM-dd",
  defaultValue = initialValue,
  shorthandOperators = initialOperators,
  timeFormat = "HH:mm",
} = {}) => {
  // State Hooks
  const [dateTimeValues, setDateTimeValues] = useState(defaultValue);

  const combineDateTime = (date, time) => {
    return DateTime.fromFormat(
      `${date} ${time}`,
      `${dateFormat} ${timeFormat}`
    );
  };

  // Handlers
  const handleChangeValue = (property, value) => {
    if (Object.keys(shorthandOperators).includes(property)) {
      // The property matches one of our shorthand functions
      setDateTimeValues({
        ...shorthandOperators[property](dateFormat, timeFormat),
      });
    } else if (Object.keys(initialValue).includes(property) && value) {
      // The property is for one of our individual values
      setDateTimeValues((previousState) => ({
        ...previousState,
        [property]: value,
      }));
    } else {
      // The property is not a valid value
      console.warn(
        `The property ${property} is invalid. Use one of the following properties: ${Object.keys(
          initialValue
        ).join(", ")}`
      );
    }
  };

  const handleChangeValues = (value) => {
    for (const property in value) {
      if (Object.keys(initialValue).includes(property) && value[property]) {
        // The property is for one of our individual values
        setDateTimeValues((previousState) => ({
          ...previousState,
          [property]: value[property],
        }));
      } else {
        // The property is not a valid value
        console.warn(
          `The value ${value[property]} for ${property} is invalid.`
        );
      }
    }
  };

  const endDateTime = combineDateTime(
    dateTimeValues.endDate,
    dateTimeValues.endTime
  );

  const startDateTime = combineDateTime(
    dateTimeValues.startDate,
    dateTimeValues.startTime
  );

  const error = useMemo(() => {
    let errorMessage;

    if (!startDateTime.isValid) {
      errorMessage =
        "Cannot calculate a start datetime. The start date or start time is invalid.";
    } else if (!endDateTime.isValid) {
      errorMessage =
        "Cannot calculate an end datetime. The end date or end time is invalid.";
    } else if (endDateTime < startDateTime) {
      errorMessage = "Start time cannot be after the end time";
    }

    return errorMessage;
  }, [endDateTime, startDateTime]);

  return {
    ...dateTimeValues,
    error,
    endDateTime: combineDateTime(
      dateTimeValues.endDate,
      dateTimeValues.endTime
    ),
    isError: !!error,
    startDateTime: combineDateTime(
      dateTimeValues.startDate,
      dateTimeValues.startTime
    ),
    setValue: handleChangeValue,
    setValues: handleChangeValues,
  };
};

export default useDateTimeRange;
